import React, { useState, useRef, useEffect } from 'react';
import styles from './section.module.css';
import { aniLogo, sectionImg1, HomeV, HomeVImg, HomeV1, homeLogoImg } from '../../../assets'; // Replace `HomeV` with your actual video source
import { Link } from 'react-router-dom';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { MotionPathPlugin } from 'gsap/MotionPathPlugin';
import { FaPlay } from 'react-icons/fa'; // Play icon from react-icons
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import useTextAnimation from "../../hooks/useTextAnimation.js";
import { useDispatch, useSelector } from 'react-redux';
import { fetchConfigData } from '../../slice/configSlice.js';
import { useTranslation } from 'react-i18next';
import '../../../i18n';
import { LANG_AR, LANG_EN } from '../../helpers/constant.jsx';
import VideoPopup from '../videoPopup/VideoPopup.jsx';

gsap.registerPlugin(ScrollTrigger, MotionPathPlugin);

const SectionFirst = ({ configData }) => {
    const serviceSec_reelImgRef = useRef(null);
    const [isVideoOpen, setIsVideoOpen] = useState(false);
    const [videoCurrentTime, setVideoCurrentTime] = useState(0);
    const { t, i18n } = useTranslation();
   const [isVideoPopupOpen, setIsVideoPopupOpen] = useState(false);
    const [currentVideoSrc, setCurrentVideoSrc] = useState('');
    const videoPopupRef = useRef(null);
    const popupVideoRef = useRef(null);
    const videoRef = useRef(null);

    const handlePlayClick = (videoSrc) => {
        setCurrentVideoSrc(videoSrc);
        const currentVideo = videoRef.current;
        if (currentVideo) {
            //setVideoCurrentTime(currentVideo.currentTime);  // Store the current time of the video when clicked
        }
        setIsVideoPopupOpen(true);
    };

    const handleCloseClick = () => {
        setIsVideoPopupOpen(false);
    };

    useEffect(() => {
        const path = document.querySelector(`.${styles.lineB} path`);
        const pathLength = path?.getTotalLength();
        const serviceIcon = document.querySelector(`.${styles.serviceIcon}`);
        const windowWidth = window.innerWidth;

        if (!path || windowWidth < 800) {
            return; // Skip animations for small screens
        }

        // Path animation
        const pathAnimation = gsap.fromTo(
            path,
            { strokeDasharray: pathLength, strokeDashoffset: pathLength },
            {
                strokeDashoffset: 0,
                ease: 'none',
                scrollTrigger: {
                    trigger: path,
                    start: 'top 40%',
                    end: 'bottom 80%',
                    scrub: 1.5,
                    markers: false,
                },
            }
        );

        // Service reel image animation
        const reelAnimation = gsap.fromTo(
            serviceSec_reelImgRef.current,
            { scale: 0, x: -pathLength * 0.05 },
            {
                scale: 1,
                x: 0,
                ease: 'power2.out',
                scrollTrigger: {
                    trigger: path,
                    start: 'top bottom',
                    end: 'bottom top',
                    scrub: 0.5,
                    markers: false,
                },
            }
        );

        // Service icon animation
        const logoAnimation = gsap.fromTo(
            serviceIcon,
            { scale: 0.5, opacity: 0, x: -pathLength * 0.05, y: pathLength * 0.05 },
            {
                scale: 1,
                opacity: 1,
                x: 0,
                y: 0,
                ease: 'power2.out',
                scrollTrigger: {
                    trigger: path,
                    start: '70% bottom', // Start animation when the path is at the bottom of the viewport
                    end: 'center center', // End animation when the path reaches the center of the viewport
                    scrub: 0.5,
                    markers: false, // Set to true if you want to debug animation positions
                },
            }
        );

        // Resize handler to update animations
        const handleResize = () => {
            const updatedWindowWidth = window.innerWidth;
            if (updatedWindowWidth < 800) {
                // Kill animations if screen width is below 800px
                pathAnimation.kill();
                reelAnimation.kill();
                logoAnimation.kill()
                ScrollTrigger.getAll().forEach((trigger) => trigger.kill());
            }
        };

        window.addEventListener('resize', handleResize);

        return () => {
            // Clean up animations and event listeners
            pathAnimation.kill();
            reelAnimation.kill();
            logoAnimation.kill();
            ScrollTrigger.getAll().forEach((trigger) => trigger.kill());
            window.removeEventListener('resize', handleResize);
        };
    }, []);

    const { ref: headingRef, inView: headingInView, textAnimation } = useTextAnimation();
    const { ref: paragraphRef, inView: paragraphInView } = useTextAnimation();
    const { ref: linkRef, inView: linkInView } = useTextAnimation();

    useEffect(() => {
        const handleOutsideClick = (e) => {
            if (videoPopupRef.current && !videoPopupRef.current.contains(e.target)) {
                setIsVideoOpen(false);
            }
        };

        if (isVideoOpen) {
            document.addEventListener('mousedown', handleOutsideClick);
        } else {
            document.removeEventListener('mousedown', handleOutsideClick);
        }

        return () => {
            document.removeEventListener('mousedown', handleOutsideClick);
        };
    }, [isVideoOpen]);


    useEffect(() => {
        const videoElement = videoRef.current;

        const observerCallback = (entries) => {
            entries.forEach((entry) => {
                if (entry.isIntersecting) {
                    // Play muted initially
                    videoElement.muted = true;
                    videoElement.play().catch((error) => {
                        console.log("Video autoplay error:", error);
                    });
                } else {
                    videoElement.pause();
                }
            });
        };

        const observer = new IntersectionObserver(observerCallback, {
            threshold: 0.5, // Adjust threshold as needed
        });

        if (videoElement) {
            observer.observe(videoElement);
        }

        return () => {
            if (videoElement) {
                observer.unobserve(videoElement);
            }
        };
    }, []);

    const toggleSound = () => {
        const videoElement = videoRef.current;
        if (videoElement.muted) {
            videoElement.muted = false; // Unmute
        } else {
            videoElement.muted = true; // Mute
        }
        videoElement.play().catch((error) => {
            console.log("Video playback error:", error);
        });
    };

    return (
        <>
            <div className={styles.serviceSec_inner}>
                <svg
                    className={styles.lineB}
                    width="937"
                    height="1109"
                    viewBox="0 0 937 1109"
                    fill="none"
                    preserveAspectRatio="xMidYMax meet"
                >
                    <defs>
                        <linearGradient id="linear-gradient" x1="0" y1="0" x2="1" y2="1" gradientUnits="objectBoundingBox">
                            <stop offset="0" stopColor="#3c5bea" />
                            <stop offset="1" stopColor="#dd043d" />
                        </linearGradient>
                    </defs>
                    <path
                        d="M-43 12.2652C-42.1915 5.753 -21.8023 15.275 -20.0409 16.0438C1.50456 25.4483 21.5773 38.0316 41.9927 49.6068C78.7577 70.4521 116.194 90.1234 152.594 111.621C235.238 160.429 310.431 215.818 378.211 284.061C429.665 335.868 478.052 396.352 501.836 466.366C519.418 518.121 523.727 571.632 526.341 625.957C528.957 680.343 527.173 735.846 516.406 789.326C505.883 841.596 474.419 900.899 433.842 936.205C393.609 971.213 378.211 974.214 334.831 986.259C272.908 1000.22 219.014 989.491 170.586 963.211C133.622 943.153 103.107 906.251 100.825 861.453C94.6375 739.965 273.572 740.639 352.823 758.431C407.229 770.645 461.178 791.982 509.673 819.555C560.664 848.547 605.752 887.21 652.616 922.245C738.904 986.754 830.146 1049.54 926.137 1098.73"
                        stroke="url(#linear-gradient)"
                        strokeWidth="20"
                        strokeLinecap="round"
                    />
                </svg>
                <div className={styles.serviceFlex}>
                    {/* <div className={styles.serviceIcon}>
                        <img src={aniLogo} alt="" />
                    </div> */}
                    <div className={`${styles.serviceDetail} ${i18n.language === LANG_AR ? "arabText" : ""}`}>
                        <div className={styles.serviceDetailT}>
                            <motion.h3
                                ref={headingRef}
                                initial="hidden"
                                animate={headingInView ? "visible" : "hidden"}
                                custom={0}
                                variants={textAnimation}
                                //dangerouslySetInnerHTML={{ __html: `${t('homeWeAreTitle')}`}}
                                dangerouslySetInnerHTML={{ 
                                    __html: i18n.language === LANG_EN ? configData.homeTitle : configData.homeTitleAr 
                                }}
                            >
                                {/* {configData.homeTitle} */}
                                {/* {i18n.language === LANG_EN ? configData.homeTitle : configData.homeTitleAr} */}
                                {/* <span>(</span>we are<span>)</span> */}
                            </motion.h3>
                            <div className={styles.serviceDetailP}>
                                <motion.p
                                    ref={paragraphRef}
                                    initial="hidden"
                                    animate={paragraphInView ? "visible" : "hidden"}
                                    custom={1}
                                    variants={textAnimation}
                                //dangerouslySetInnerHTML={{ __html: configData.homeDescription }}
                                 dangerouslySetInnerHTML={{
                                    __html: i18n.language === LANG_EN ? configData.homeDescription : configData.homeDescriptionAr
                                 }}
                                //dangerouslySetInnerHTML={{ __html: `${t('homeWeAreDesc')}`}}
                                >
                                   {/*  <strong>A young, independent</strong> creative agency, born in <strong>Abu Dhabi</strong>, with a people-first philosophy, breaking the mold of traditional client-agency dynamics.<br /><br /> We focus all our energy on mutual collaboration that ignites growth and provides brands with the <strong>magnitude</strong> they need through <strong>strategic wisdom and creative power.</strong> */}
                                </motion.p>
                            </div>
                            <div className={styles.serviceDetailLnk}>
                                <Link to={configData.homeUrl ? configData.homeUrl : "/about-us"}>
                                    <motion.div className={styles.serviceDetailLk}
                                        ref={linkRef}
                                        initial="hidden"
                                        animate={linkInView ? "visible" : "hidden"}
                                        custom={3}
                                        variants={textAnimation}>
                                        {/*  <p>{configData.homeUrlTitle}</p> */}
                                        <p>{i18n.language === LANG_EN ? configData.homeUrlTitle : configData.homeUrlTitleAr}</p>
                                        <span>
                                            <svg width="20.123" height="19.718" viewBox="0 0 20.123 19.718">
                                                <defs>
                                                    <clipPath id="clip-path">
                                                        <rect id="Rectangle_2464" data-name="Rectangle 2464" width="20.123" height="19.718" fill="none" />
                                                    </clipPath>
                                                </defs>
                                                <g id="Group_9348" data-name="Group 9348" transform="translate(0 0)">
                                                    <g id="Group_9347" data-name="Group 9347" transform="translate(0 0)" clipPath="url(#clip-path)">
                                                        <path
                                                            id="Path_13955"
                                                            data-name="Path 13955"
                                                            d="M2.281,19.305a1.468,1.468,0,0,1,.035-1.565L15.2,2.576l-13.75.615A1.214,1.214,0,0,1,.976.811L18.1,0a1.114,1.114,0,0,1,1.219,1.109l.806,17.123a1.177,1.177,0,0,1-2.3.254l-.8-14.251L4.14,19.4a1.61,1.61,0,0,1-1.479.252Z"
                                                            transform="translate(0 0)"
                                                            fill="#DD043D"
                                                        />
                                                    </g>
                                                </g>
                                            </svg>
                                        </span>
                                    </motion.div>
                                </Link>
                            </div>
                        </div>
                        <div className={styles.serviceDetailDisc}>
                            <img src={homeLogoImg} alt="" />
                        </div>
                    </div>
                </div>
            </div>
            <div className={styles.serviceSec_reel}>
                <div className={styles.serviceSec_reelImg} ref={serviceSec_reelImgRef}>
                    <video
                        ref={videoRef}
                        loop
                        playsInline
                        muted
                        // style={{ cursor: "pointer" }}
                        onClick={() => handlePlayClick(HomeV1)}
                        controls={false}
                        poster={HomeVImg}
                    >
                        <source src={HomeV1} type="video/mp4" />
                        Your browser does not support the video tag.
                    </video>
                    <img
                        src={HomeVImg}
                        alt="Fallback"
                        style={{ display: 'none', width: '100%', height: 'auto' }}
                    />
                </div>
            </div>

{/*             {isVideoOpen && (
                <div className={styles.videoPopup} ref={videoPopupRef}>
                    <button className={styles.closeButton} onClick={handleCloseClick}>
                        ✖
                    </button>
                    <div className={styles.videoPopupContent}>
                        <video
                            ref={popupVideoRef}
                            controls
                            autoPlay
                            src={HomeV}  // Replace with your video URL
                        />
                        <button onClick={handleCloseClick}>Close</button>
                    </div>
                </div>
            )} */}


            {isVideoPopupOpen && (
                            <VideoPopup videoPopupRef={videoPopupRef} handleCloseClick={handleCloseClick} popupVideoRef={popupVideoRef} currentVideoSrc={currentVideoSrc}/>
            
                        )}
        </>
    );
};

export default SectionFirst;
